import { useMemo, useState } from 'react';
import { Badge, Button, FileInput, Group, Select, Stack, Table, Text, Title } from '@mantine/core';
import { useNavigate } from 'react-router';
import {
  useCiktiIndir,
  useEksikSaglikKanaati,
  useIbysDisaAktar,
  useSaglikListesi,
  useSaglikKanaatYukle,
  useSaglikListesiIndir,
  useSaglikOptions,
} from '../api/queries';
import { BosListeNotu, QueryStatus } from './parts';
import { Sayfalama, SuzgecGostergesi } from '../sozlesme';
import { ANLAM } from '../theme';
import { MUAYENE_ASAMASI_ETIKETI } from '@isg/validation';
import { tarihGoster } from '../lib/format';

/**
 * Sağlık kanaati listesi (F3B·S8 — Kapsam §16.11).
 *
 * ⚠️ Bu ekranı **izleyici ve saha sorumlusu GÖREMEZ** (§16.11/10 — dar okuma
 * listesi; izleyici salt-okunurluğunun İLK ve TEK istisnası). Karar
 * SUNUCUDADIR: menü görünürlüğü yalnız sadeleştirir, uç 403 verir (§2.16/B3).
 *
 * Vade işaretleri SUNUCUDAN gelir ve **TÜRETİLMİŞTİR** (kolon değil —
 * `kazaDosyasiEksikMi` deseni); ekran yeniden hesaplamaz.
 *
 * **GEREKÇE YOKTUR** (S1): listede tanı/tetkik/bulgu alanı yoktur ve
 * eklenmez — sistemde tutulan yalnız KANAATTİR.
 */
export function SaglikScreen(): React.JSX.Element {
  const options = useSaglikOptions();
  const navigate = useNavigate();
  const [isyeri, setIsyeri] = useState<string | null>(null);
  const [vade, setVade] = useState<string | null>(null);
  const [sayfa, setSayfa] = useState(1);
  const [yuklemeAcik, setYuklemeAcik] = useState(false);
  // "Kaydı yok" süzgeci AYRI BİR SORGU SINIFIDIR (yokluk sorgusu — F4·S1):
  // kanaat listesi olmayanı gösteremez. Ayrı pano AÇILMADI (açık madde A3);
  // sunucu kümesi günlük özetin saydığı kümenin ta kendisidir.
  const kaydiYokModu = vade === 'kaydi_yok';
  const eksikler = useEksikSaglikKanaati(kaydiYokModu);
  // P1: süzgeç + sayfalama SUNUCUDA (ekipman Y3 / yüklenici Y1 deseni).
  const liste = useSaglikListesi({
    workplaceId: isyeri,
    vade: kaydiYokModu ? null : (vade as 'dolmus' | 'yaklasan' | null),
    sayfa,
  });
  const rows = liste.data?.satirlar ?? [];
  // P2: EŞİK SUNUCUDAN — ekranda `30` sabiti YAŞAMAZ (K7 marj dersi).
  const esik = liste.data?.uyariEsigiGun ?? null;
  const ibys = useIbysDisaAktar();
  const excel = useSaglikListesiIndir();

  const isyerleri = useMemo(
    () =>
      (options.data?.workplaces ?? []).map((w) => ({
        value: w.id,
        label: `${w.companyAd} · ${w.ad}`,
      })),
    [options.data?.workplaces],
  );
  const etkinSuzgec = (isyeri ? 1 : 0) + (vade ? 1 : 0);

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Sağlık gözetimi</Title>
        <Group>
          <Button
            variant="default"
            data-testid="saglik-liste-excel"
            loading={excel.isPending}
            onClick={() =>
              excel.mutate({
                workplaceId: isyeri,
                vade: vade === 'dolmus' || vade === 'yaklasan' ? vade : null,
              })
            }
          >
            Listeyi Excel’e aktar
          </Button>
          <Button
            variant="default"
            data-testid="saglik-disa-aktar"
            loading={ibys.isPending}
            onClick={() => ibys.mutate({ workplaceId: isyeri })}
          >
            İBYS düzeninde dışa aktar
          </Button>
          {/* P6 — TOPLU AKTARMA: kapı tekil kayıt açmayla AYNI (sağlık verisi
              yazma kapısı dar kalır; holding yöneticisi listede varsayılan
              olarak YOKTUR). */}
          {options.data?.kayitAcabilir && (
            <Button
              variant="default"
              data-testid="saglik-yukleme-ac"
              onClick={() => setYuklemeAcik((v) => !v)}
            >
              Toplu aktarma
            </Button>
          )}
          {options.data?.kayitAcabilir && (
            <Button data-testid="kanaat-ac" onClick={() => void navigate('/saglik/yeni')}>
              Yeni kanaat
            </Button>
          )}
        </Group>
      </Group>
      <Text size="sm" c="dimmed">
        Sistemde muayene <b>kanaati</b> tutulur — Ek-2’nin içi, bulgular ve tetkik sonuçları
        girilmez. Kanaat kişiye asılıdır; kısıtlar iş izni uygunluk kontrolüne girdidir.
      </Text>
      <Group>
        <Select
          placeholder="İşyeri"
          clearable
          data-testid="saglik-suzgec-isyeri"
          data={isyerleri}
          value={isyeri}
          onChange={(v) => {
            setIsyeri(v);
            setSayfa(1);
          }}
        />
        <Select
          placeholder="Vade"
          clearable
          data-testid="saglik-suzgec-vade"
          data={[
            { value: 'dolmus', label: 'Vadesi dolmuş' },
            {
              value: 'yaklasan',
              // Eşik SUNUCUDAN — etiket de onu söyler (ekranda sabit yok).
              label: esik === null ? 'Vadesi yaklaşan' : `${esik} gün içinde dolacak`,
            },
            { value: 'kaydi_yok', label: 'Kaydı yok (hiç kanaat girilmemiş)' },
          ]}
          value={vade}
          onChange={(v) => {
            setVade(v);
            setSayfa(1);
          }}
        />
        <SuzgecGostergesi
          etkinSayisi={etkinSuzgec}
          testId="saglik-suzgec-gostergesi"
          onTemizle={() => {
            setIsyeri(null);
            setVade(null);
            setSayfa(1);
          }}
        />
      </Group>
      {/* P3 — İNDİRME HATASI GÖRÜNÜR: eskiden `if (!yanit.ok) return` ile
          sessizce yutuluyordu ve kullanıcı düğmeyi bozuk sanıyordu. */}
      {(ibys.isError || excel.isError) && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="saglik-indirme-hatasi">
          {(ibys.error ?? excel.error)?.message ?? 'İndirme başarısız oldu.'}
        </Text>
      )}
      {options.data?.kayitAcabilir && yuklemeAcik && <SaglikYuklemePaneli />}
      {kaydiYokModu ? (
        <>
          <QueryStatus queries={[eksikler]} />
          {eksikler.isSuccess && eksikler.data.length === 0 && (
            <BosListeNotu mesaj="Aktif istihdamlı herkesin sağlık kanaati kayıtlı." />
          )}
          {(eksikler.data?.length ?? 0) > 0 && (
            <Table striped data-testid="saglik-eksik-listesi">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Kişi</Table.Th>
                  <Table.Th>İşveren</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(eksikler.data ?? []).map((satir) => (
                  <Table.Tr key={satir.personId} data-testid={`saglik-eksik-${satir.personId}`}>
                    <Table.Td>{satir.adSoyad}</Table.Td>
                    <Table.Td>{satir.isverenAd}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </>
      ) : null}
      {!kaydiYokModu && <QueryStatus queries={[liste, options]} />}
      {!kaydiYokModu && liste.isSuccess && rows.length === 0 && (
        <BosListeNotu mesaj="Görüntüleyebileceğiniz sağlık kanaati yok." />
      )}
      {!kaydiYokModu && rows.length > 0 && (
        <Table striped highlightOnHover data-testid="saglik-listesi">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Kişi</Table.Th>
              <Table.Th>İşyeri</Table.Th>
              <Table.Th>Muayene</Table.Th>
              <Table.Th>Tarih</Table.Th>
              <Table.Th>Geçerlilik</Table.Th>
              <Table.Th>Karar</Table.Th>
              <Table.Th>Belge</Table.Th>
              <Table.Th>Çalışma koşulları</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((kayit) => (
              <Table.Tr
                key={kayit.id}
                style={{ cursor: 'pointer' }}
                data-testid={`kanaat-satir-${kayit.id}`}
                onClick={() => void navigate(`/saglik/${kayit.id}`)}
              >
                <Table.Td>{kayit.personAd}</Table.Td>
                <Table.Td>{kayit.workplaceAd}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm">{kayit.muayeneTuru}</Text>
                    {/* S1: aşama TÜRETİLİR — seçilmez, sunucudan gelir. */}
                    <Badge
                      variant="light"
                      size="sm"
                      color={ANLAM.bilgi}
                      data-testid={`kanaat-asama-${kayit.id}`}
                    >
                      {MUAYENE_ASAMASI_ETIKETI[kayit.muayeneAsamasi]}
                    </Badge>
                  </Group>
                </Table.Td>
                <Table.Td>{tarihGoster(kayit.muayeneTarihi)}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm">{tarihGoster(kayit.gecerlilikBitis)}</Text>
                    {/* TÜRETİLMİŞ işaretler — sunucudan gelir. */}
                    {kayit.vadesiDoldu && (
                      <Badge color={ANLAM.engel} size="sm" data-testid="kanaat-vade-dolmus">
                        vadesi doldu
                      </Badge>
                    )}
                    {!kayit.vadesiDoldu && esik !== null && kayit.gunKaldi <= esik && (
                      <Badge color={ANLAM.dikkat} size="sm">
                        {kayit.gunKaldi} gün
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge color={KARAR_RENGI[kayit.kararSinifi ?? 'yok']} size="sm">
                    {kayit.uygunlukKarari}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {/* P6 — TÜRETİLMİŞ işaret: kaydın canlı belgesi var mı.
                      Toplu aktarma dosya taşımaz; elle açılmış belgesiz
                      kayıt da aynı rozeti alır (soru "nasıl girildi" değil,
                      "belgesi var mı"). */}
                  {kayit.belgesiz && (
                    <Badge
                      size="sm"
                      variant="light"
                      color={ANLAM.dikkat}
                      data-testid={`kanaat-belgesiz-${kayit.id}`}
                    >
                      belgesiz
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td data-testid={`kanaat-kisitlar-${kayit.id}`}>
                  {kayit.kisitlar.length === 0
                    ? '—'
                    : kayit.kisitlar.map((k) => k.ifade).join(' · ')}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {!kaydiYokModu && liste.data && (
        <Sayfalama
          sayfa={liste.data.sayfa}
          sayfaBoyutu={liste.data.sayfaBoyutu}
          toplam={liste.data.toplam}
          onDegis={setSayfa}
          testId="saglik-sayfalama"
        />
      )}
    </Stack>
  );
}

/** Karar SINIFINDAN okunur (kalem adından DEĞİL — §19/11-10). */
const KARAR_RENGI: Record<string, string> = {
  uygun: ANLAM.olumlu,
  kisitli: ANLAM.dikkat,
  uygun_degil: ANLAM.engel,
  yok: ANLAM.bilgi,
};

/**
 * P6 — GEÇMİŞ MUAYENE DURUMUNUN TOPLU AKTARIMI (tur 7).
 *
 * ⚠️ **BELGE AKTARILMAZ** — Excel'den rapor/tarama gelmez; açılan kayıtlar
 * listede *"belgesiz"* rozetiyle görünür (işaret TÜRETİLİR, kolon yok).
 * ⚠️ **ÇOKLU ve SIFIR eşleşmede satır REDDEDİLİR** — sağlık verisinde tahmin
 * yürütülmez; rapor satırı ne yapılacağını söyler.
 */
function SaglikYuklemePaneli(): React.JSX.Element {
  const yukle = useSaglikKanaatYukle();
  const indir = useCiktiIndir();
  const [dosya, setDosya] = useState<File | null>(null);
  return (
    <Stack gap="sm" data-testid="saglik-yukleme-paneli">
      <Group align="flex-end">
        <Button
          variant="default"
          size="xs"
          data-testid="saglik-sablon-indir"
          loading={indir.isPending}
          onClick={() =>
            indir.mutate({
              yol: '/api/health-opinions/import/template',
              yedekAd: 'saglik-kanaati-sablonu.xlsx',
            })
          }
        >
          Şablonu indir
        </Button>
        <FileInput
          data-testid="saglik-yukleme-dosya"
          placeholder="Doldurulmuş şablonu seçin (.xlsx / .csv)"
          accept=".xlsx,.csv"
          value={dosya}
          onChange={(secilen) => {
            setDosya(secilen);
            yukle.reset();
          }}
          clearable
          size="xs"
          w={320}
        />
        <Button
          size="xs"
          data-testid="saglik-yukle"
          disabled={!dosya}
          loading={yukle.isPending}
          onClick={() => dosya && yukle.mutate({ dosya })}
        >
          Yükle
        </Button>
      </Group>
      <Text size="xs" c="dimmed">
        Belge aktarılmaz — açılan kayıtlar “belgesiz” olarak işaretlenir.
      </Text>
      {yukle.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="saglik-yukleme-hata">
          {yukle.error.message}
        </Text>
      )}
      {yukle.data && (
        <Stack gap="xs">
          <Group gap="xs" data-testid="saglik-yukleme-ozeti">
            <Badge color={ANLAM.olumlu} variant="light">
              Eklendi: {yukle.data.eklenen}
            </Badge>
            <Badge color={ANLAM.engel} variant="light">
              İşlenemedi: {yukle.data.hatali}
            </Badge>
          </Group>
          <Table striped data-testid="saglik-yukleme-sonuc-tablosu">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={70}>Satır</Table.Th>
                <Table.Th>Kişi</Table.Th>
                <Table.Th>Sonuç</Table.Th>
                <Table.Th>Açıklama</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {yukle.data.satirlar.map((satir) => (
                <Table.Tr key={satir.satir} data-testid={`saglik-yukleme-satir-${satir.satir}`}>
                  <Table.Td>{satir.satir}</Table.Td>
                  <Table.Td>{satir.ad ?? '—'}</Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      variant="light"
                      color={satir.sonuc === 'eklendi' ? ANLAM.olumlu : ANLAM.engel}
                    >
                      {satir.sonuc === 'eklendi' ? 'Eklendi' : 'İşlenemedi'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{satir.mesaj ?? satir.uyarilar.join(' · ')}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      )}
    </Stack>
  );
}
